/**
 * Image upload service.
 *
 * API-first: when the Node backend is configured the file is POSTed as
 * multipart/form-data to `POST /uploads/image`, which re-validates type, MIME,
 * size and dimensions before storing it outside the web root and returning the
 * public URL.
 *
 * Demo mode (VITE_PUBLIC_API_URL empty): the validated file is turned into a
 * local preview URL so the whole flow stays usable in preview.
 */

import { API_BASE_URL, ApiError, isApiConfigured } from "./http";

export type UploadFolder =
  | "listings"
  | "gallery"
  | "ads"
  | "logos"
  | "avatars"
  | "media"
  | "branding";

/** Branding asset kinds accepted by the settings uploads. */
export type BrandingAssetKind = "favicon" | "font";

export interface UploadImageOptions {
  token?: string | null;
  signal?: AbortSignal;
}

interface UploadEnvelope {
  success?: boolean;
  data?: { url?: string; width?: number; height?: number };
  message?: string;
}

/** Upload a single validated image and resolve to its served URL. */
export async function uploadImage(
  file: File,
  folder: UploadFolder,
  options: UploadImageOptions = {},
): Promise<string> {
  if (!isApiConfigured()) {
    // Demo mode — a real object URL keeps previews and page renders working.
    await new Promise((resolve) => setTimeout(resolve, 420));
    return URL.createObjectURL(file);
  }

  const body = new FormData();
  body.append("file", file, file.name);
  body.append("folder", folder);

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/uploads/image`, {
      method: "POST",
      headers: options.token ? { Authorization: `Bearer ${options.token}` } : undefined,
      body,
      signal: options.signal,
    });
  } catch {
    throw new ApiError("Upload failed — please check your connection and try again.", 0);
  }

  const responseText = await response.text();
  let parsed: UploadEnvelope | null = null;
  try {
    parsed = JSON.parse(responseText) as UploadEnvelope;
  } catch {
    parsed = null;
  }

  const url = parsed?.data?.url;
  if (!response.ok || !url) {
    throw new ApiError(parsed?.message ?? "Upload failed. Please try again.", response.status, parsed);
  }
  return url;
}

/** Best-effort cleanup for object URLs created in demo mode. */
export function releasePreviewUrl(url: string): void {
  if (url.startsWith("blob:")) URL.revokeObjectURL(url);
}

/**
 * Upload a branding asset (a favicon image or a custom web font) and resolve to
 * its served URL. Fonts are stored under /fonts, images under /branding.
 */
export async function uploadBrandingAsset(
  file: File,
  kind: BrandingAssetKind,
  options: UploadImageOptions = {},
): Promise<string> {
  if (!isApiConfigured()) {
    await new Promise((resolve) => setTimeout(resolve, 420));
    return URL.createObjectURL(file);
  }

  const body = new FormData();
  body.append("file", file, file.name);
  body.append("folder", kind === "font" ? "fonts" : "branding");

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/uploads/branding`, {
      method: "POST",
      headers: options.token ? { Authorization: `Bearer ${options.token}` } : undefined,
      body,
      signal: options.signal,
    });
  } catch {
    throw new ApiError("Upload failed — please check your connection and try again.", 0);
  }

  const responseText = await response.text();
  let parsed: UploadEnvelope | null = null;
  try {
    parsed = JSON.parse(responseText) as UploadEnvelope;
  } catch {
    parsed = null;
  }

  const url = parsed?.data?.url;
  if (!response.ok || !url) {
    throw new ApiError(parsed?.message ?? "Upload failed. Please try again.", response.status, parsed);
  }
  return url;
}